import React, { useContext, useState } from 'react';
import { Button, TabsProps } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import UserActions from '@totem/components/users/container/actions/UserActions';
import { User } from '@totem/components/users/container/types';
import UsersTable from '@totem/components/users/container/userContainerTable';
import UsersContainer from '@totem/components/users/container/UsersContainer';
import UserSearch from '@totem/components/users/container/userSearch';
import UsersEmbeddedContainer from '@totem/components/users/container/UsersEmbeddedContainer';

const UserManagement = () => {
  const { userProfile } = useContext(UserProfileContext);
  const [selectedUser, setSelectedUser] = useState<User>(null);
  const [activeAction, setActiveAction] = useState<string>('');
  const [actionSource, setActionSource] = useState<string>('');
  const [refresh, setRefresh] = useState<boolean>(false);

  const [emailSearchTerm, setEmailSearchTerm] = useState<string>('');
  const [firstNameSearchTerm, setFirstNameSearchTerm] = useState<string>('');
  const [lastNameSearchTerm, setLastNameSearchTerm] = useState<string>('');

  const userCreationEnabled =
    permissions.organizationalAdministrator.hasPermissions(userProfile);

  const handleTabSwitch = (tabgroup: string) => {
    setActionSource(tabgroup);

    setRefresh(true);
  };

  const handleAction = (source: string, action: string, user: User) => {
    setActionSource(source);

    setSelectedUser(user);

    setActiveAction(action);
  };

  const handleActionClosed = (doRefresh: boolean) => {
    setActiveAction('');

    setSelectedUser(null);

    setRefresh(doRefresh);
  };

  const handleDataRefreshRequested = (source: string) => {
    if (source === actionSource && refresh) {
      setRefresh(false);
    }
  };

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'customerAccounts',
        label: 'Users',
        children: (
          <UsersContainer
            staticFilters={{
              tabGroup: 'customerAccounts',
              searchEmail: emailSearchTerm,
              searchFirstName: firstNameSearchTerm,
              searchLastName: lastNameSearchTerm,
            }}
            actions={[
              {
                name: 'removeUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'removeIBUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'viewDetails',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'editUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'dataPermissions',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'securityGroups',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'activateUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
            ]}
            onDataRefreshRequested={() =>
              handleDataRefreshRequested('customerAccounts')
            }
            refresh={actionSource === 'customerAccounts' && refresh}
          >
            <UsersTable />
          </UsersContainer>
        ),
      },
      {
        key: 'tenantAccounts',
        label: 'Tenants',
        children: (
          <UsersEmbeddedContainer
            staticFilters={{
              tabGroup: 'tenantAccounts',
              searchEmail: emailSearchTerm,
              searchFirstName: firstNameSearchTerm,
              searchLastName: lastNameSearchTerm,
            }}
            actions={[
              {
                name: 'removeUser',
                action: (action: string, user: User) =>
                  handleAction('tenantAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'removeIBUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'viewDetails',
                action: (action: string, user: User) =>
                  handleAction('tenantAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'editUser',
                action: (action: string, user: User) =>
                  handleAction('tenantAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'dataPermissions',
                action: (action: string, user: User) =>
                  handleAction('tenantAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'securityGroups',
                action: (action: string, user: User) =>
                  handleAction('tenantAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'activateUser',
                action: (action: string, user: User) =>
                  handleAction('tenantAccounts', action, user),
                replaceDefault: true,
              },
            ]}
            onDataRefreshRequested={() =>
              handleDataRefreshRequested('tenantAccounts')
            }
            refresh={actionSource === 'tenantAccounts' && refresh}
          >
            <UsersTable />
          </UsersEmbeddedContainer>
        ),
      },
      {
        key: 'vendorAccounts',
        label: 'Vendors',
        children: (
          <UsersEmbeddedContainer
            staticFilters={{
              tabGroup: 'vendorAccounts',
              searchEmail: emailSearchTerm,
              searchFirstName: firstNameSearchTerm,
              searchLastName: lastNameSearchTerm,
            }}
            actions={[
              {
                name: 'removeUser',
                action: (action: string, user: User) =>
                  handleAction('vendorAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'removeIBUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'viewDetails',
                action: (action: string, user: User) =>
                  handleAction('vendorAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'editUser',
                action: (action: string, user: User) =>
                  handleAction('vendorAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'dataPermissions',
                action: (action: string, user: User) =>
                  handleAction('vendorAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'securityGroups',
                action: (action: string, user: User) =>
                  handleAction('vendorAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'activateUser',
                action: (action: string, user: User) =>
                  handleAction('vendorAccounts', action, user),
                replaceDefault: true,
              },
            ]}
            onDataRefreshRequested={() =>
              handleDataRefreshRequested('vendorAccounts')
            }
            refresh={actionSource === 'vendorAccounts' && refresh}
          >
            <UsersTable />
          </UsersEmbeddedContainer>
        ),
      },
    ];

    if (permissions.internal.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'internalAccounts',
        label: (
          <IBTooltip content={permissions.internal.tooltip}>
            <span>IB Users</span>
          </IBTooltip>
        ),
        children: (
          <UsersEmbeddedContainer
            staticFilters={{
              tabGroup: 'internalAccounts',
              searchEmail: emailSearchTerm,
              searchFirstName: firstNameSearchTerm,
              searchLastName: lastNameSearchTerm,
            }}
            actions={[
              {
                name: 'removeUser',
                action: (action: string, user: User) =>
                  handleAction('internalAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'removeIBUser',
                action: (action: string, user: User) =>
                  handleAction('customerAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'viewDetails',
                action: (action: string, user: User) =>
                  handleAction('internalAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'editUser',
                action: (action: string, user: User) =>
                  handleAction('internalAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'dataPermissions',
                action: (action: string, user: User) =>
                  handleAction('internalAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'securityGroups',
                action: (action: string, user: User) =>
                  handleAction('internalAccounts', action, user),
                replaceDefault: true,
              },
              {
                name: 'activateUser',
                action: (action: string, user: User) =>
                  handleAction('internalAccounts', action, user),
                replaceDefault: true,
              },
            ]}
            onDataRefreshRequested={() =>
              handleDataRefreshRequested('internalAccounts')
            }
            refresh={actionSource === 'internalAccounts' && refresh}
          >
            <UsersTable />
          </UsersEmbeddedContainer>
        ),
      });
    }

    tabItems.push({
      key: 'inActiveAccounts',
      label: 'Disabled Users',
      children: (
        <UsersEmbeddedContainer
          staticFilters={{
            tabGroup: 'inActiveAccounts',
            searchEmail: emailSearchTerm,
            searchFirstName: firstNameSearchTerm,
            searchLastName: lastNameSearchTerm,
          }}
          actions={[
            {
              name: 'removeUser',
              action: (action: string, user: User) =>
                handleAction('inActiveAccounts', action, user),
              replaceDefault: true,
            },
            {
              name: 'removeIBUser',
              action: (action: string, user: User) =>
                handleAction('customerAccounts', action, user),
              replaceDefault: true,
            },
            {
              name: 'viewDetails',
              action: (action: string, user: User) =>
                handleAction('inActiveAccounts', action, user),
              replaceDefault: true,
            },
            {
              name: 'editUser',
              action: (action: string, user: User) =>
                handleAction('inActiveAccounts', action, user),
              replaceDefault: true,
            },
            {
              name: 'dataPermissions',
              action: (action: string, user: User) =>
                handleAction('inActiveAccounts', action, user),
              replaceDefault: true,
            },
            {
              name: 'securityGroups',
              action: (action: string, user: User) =>
                handleAction('inActiveAccounts', action, user),
              replaceDefault: true,
            },
            {
              name: 'activateUser',
              action: (action: string, user: User) =>
                handleAction('inActiveAccounts', action, user),
              replaceDefault: true,
            },
          ]}
          onDataRefreshRequested={() =>
            handleDataRefreshRequested('inActiveAccounts')
          }
          refresh={actionSource === 'inActiveAccounts' && refresh}
        >
          <UsersTable />
        </UsersEmbeddedContainer>
      ),
    });

    return tabItems;
  };

  return (
    <ContentLayout>
      <IBCard
        isCovered
        title={'User Management'}
        extra={
          <Button
            onClick={() => handleAction(actionSource, 'createUser', null)}
            disabled={!userCreationEnabled}
            type="primary"
          >
            Add User
          </Button>
        }
      >
        <UserSearch
          onEmailSearchTermUpdated={setEmailSearchTerm}
          onFirstNameSearchTermUpdated={setFirstNameSearchTerm}
          onLastNameSearchTermUpdated={setLastNameSearchTerm}
        />
        <br />
        <IBTabs
          defaultActiveKey="customerAccounts"
          onTabClick={(key) => handleTabSwitch(key)}
          items={getTabItems()}
        />
      </IBCard>
      {activeAction !== '' && (
        <UserActions
          user={selectedUser}
          action={activeAction}
          onClose={handleActionClosed}
        />
      )}
    </ContentLayout>
  );
};

export default UserManagement;
